import { LocalFile } from "@/hooks/types";
import { LoadingBall } from "@ingka/loading";
import SSRIcon from "@ingka/ssr-icon";
import arrowClockwise from "@ingka/ssr-icon/paths/arrow-clockwise";
import trashCan from "@ingka/ssr-icon/paths/trash-can";
import Text from "@ingka/text";
import classNames from "classnames";
import { ProgressIndicator } from "./ProgressIndicator";
import { WarningInfo } from "./WarningInfo";

type Props = {
  localFile: LocalFile;
  mediaElement: React.ReactNode;
  onRemove: () => void;
  onRetry: () => void;
  progress?: number;
  warningMessage?: string;
  similarContent?: React.ReactNode;
  isRetrying?: boolean;
};

export function FileToUploadItem({
  localFile,
  progress,
  onRemove,
  onRetry,
  mediaElement,
  warningMessage,
  similarContent,
  isRetrying,
}: Props) {
  const { uploadStatus } = localFile;
  const isUploading =
    uploadStatus === "uploading" || uploadStatus === "pending";
  const isFailed = uploadStatus === "failed";

  return (
    <li className="rounded-sm p-space50 shadow-md">
      <div className="relative h-[10rem]">
        <div
          className={classNames("size-full", {
            "opacity-25": isUploading && progress !== undefined,
          })}
        >
          {mediaElement}
        </div>
        {isUploading && progress !== undefined && (
          <div className="absolute left-1/2 top-1/2 mt-space150 w-full -translate-x-1/2 -translate-y-1/2 px-space100">
            <ProgressIndicator progressValue={progress} />
          </div>
        )}
      </div>
      <div className="my-space100 flex items-center justify-between">
        <Text bodySize="s" className="truncate">
          {localFile.file.name}
        </Text>
        <div className="flex space-x-space50">
          {warningMessage && <WarningInfo text={warningMessage} />}
          {(isFailed || isRetrying) && (
            <button
              onClick={onRetry}
              aria-label="Retry upload"
              disabled={isRetrying}
            >
              {isRetrying ? (
                <LoadingBall size="medium" />
              ) : (
                <SSRIcon
                  paths={arrowClockwise}
                  aria-label="Retry adding file to request"
                />
              )}
            </button>
          )}
          <button onClick={onRemove} aria-label="Remove file">
            <SSRIcon paths={trashCan} />
          </button>
        </div>
      </div>
      {similarContent}
    </li>
  );
}
